import { useState, useEffect } from "react";
import { useNavigate } from "@pankod/refine-react-router-v6";
import { Helmet } from "react-helmet";
import { styled } from "@mui/system";
import { CustomButton } from "components";
import axios from "axios";

import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { Add, Delete, FileDownload } from "@mui/icons-material";
import { format } from "date-fns";
import HighlightCard from "components/charts/HighlightCard";
import { EarningProps } from "../../interfaces/earning";
import { API_BASE_URL } from "utils/api";
import { getAuthHeaders } from "utils/auth";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: "#989ea4",
  borderRadius: "20px",
}));

const csvHeaders = ["Start Date", "End Date", "Weekly Hours", "Weekly Income"];

const escapeCsvValue = (value: string | number) => {
  const stringValue = String(value ?? "");

  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const getEarningsCsv = (earnings: EarningProps[]) => {
  const rows = earnings.map((earning) => [
    format(new Date(earning.startDateOfWeek), "dd/MM/yyyy"),
    format(new Date(earning.endDateOfWeek), "dd/MM/yyyy"),
    earning.weeklyHours,
    earning.weeklyIncome,
  ]);

  return [csvHeaders, ...rows]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
};

const AllEarnings: React.FC = () => {
  const navigate = useNavigate();
  const [allEarnings, setAllEarnings] = useState<EarningProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedYear, setSelectedYear] = useState("all");
  const pageSize = 5;
  const baseURL = API_BASE_URL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${baseURL}/earnings`, {
          headers: getAuthHeaders(),
        });
        setAllEarnings(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [baseURL]);

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${baseURL}/earnings/${id}`, {
        headers: getAuthHeaders(),
      });
      setAllEarnings(allEarnings.filter((earning) => earning._id !== id));
    } catch (error) {
      console.error("Error deleting earning:", error);
    }
  };

  const handleDownloadCsv = () => {
    const sortedAllEarnings = [...allEarnings].sort(
      (a, b) =>
        new Date(b.startDateOfWeek).getTime() -
        new Date(a.startDateOfWeek).getTime()
    );
    const csv = getEarningsCsv(sortedAllEarnings);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `earnings-${format(new Date(), "yyyy-MM-dd")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const allTimeTotalEarnings = allEarnings.reduce(
    (acc, earning) => acc + Number(earning.weeklyIncome),
    0
  );
  const allTimeTotalHours = allEarnings.reduce(
    (acc, earning) => acc + Number(earning.weeklyHours),
    0
  );
  const allTimeAverageWeeklyIncome = allEarnings.length
    ? allTimeTotalEarnings / allEarnings.length
    : 0;
  const allTimeAverageWeeklyHours = allEarnings.length
    ? allTimeTotalHours / allEarnings.length
    : 0;
  const earningYears = Array.from(
    new Set(
      allEarnings
        .map((earning) => new Date(earning.endDateOfWeek).getFullYear())
        .filter((year) => Number.isFinite(year))
    )
  ).sort((a, b) => b - a);
  const filteredEarnings =
    selectedYear === "all"
      ? allEarnings
      : allEarnings.filter(
          (earning) =>
            new Date(earning.endDateOfWeek).getFullYear().toString() ===
            selectedYear
        );
  // Sort earnings by startDateOfWeek by latest earning
  const sortedEarnings = [...filteredEarnings].sort(
    (a, b) =>
      new Date(b.startDateOfWeek).getTime() -
      new Date(a.startDateOfWeek).getTime()
  );
  const earningsToShow = sortedEarnings.slice(startIndex, endIndex);

  const totalEarnings = filteredEarnings.reduce(
    (acc, earning) => acc + Number(earning.weeklyIncome),
    0
  );
  const totalHours = filteredEarnings.reduce(
    (acc, earning) => acc + Number(earning.weeklyHours),
    0
  );

  // Calculate average weekly income
  const averageHourlyRate = totalHours ? totalEarnings / totalHours : 0;
  const totalPages = Math.max(Math.ceil(filteredEarnings.length / pageSize), 1);

  useEffect(() => {
    if (loading) {
      return;
    }

    localStorage.setItem("totalRevenue", allTimeTotalEarnings.toString());
    localStorage.setItem(
      "averageWeeklyIncome",
      allTimeAverageWeeklyIncome.toString()
    );
    localStorage.setItem(
      "averageWeeklyHours",
      allTimeAverageWeeklyHours.toString()
    );
  }, [
    allTimeAverageWeeklyHours,
    allTimeAverageWeeklyIncome,
    allTimeTotalEarnings,
    loading,
  ]);

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Helmet>
        <title>Earnings</title>
      </Helmet>
      <Box mt="20px" sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        <Stack direction="column" width="100%">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h1" fontSize="2rem" fontWeight={700} color="#11142D">
              {allEarnings.length
                ? selectedYear === "all"
                  ? "Earnings"
                  : `Earnings (${selectedYear})`
              : "There are no earnings"}
            </Typography>
            {allEarnings.length > 0 && (
              <TextField
                select
                label="Range"
                size="small"
                value={selectedYear}
                onChange={(event) => {
                  setSelectedYear(event.target.value);
                  setCurrentPage(1);
                }}
                sx={{ minWidth: 150, bgcolor: "#fcfcfc" }}
              >
                <MenuItem value="all">All Time</MenuItem>
                {earningYears.map((year) => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
          <Box mt="20px" display={"flex"} flexWrap={"wrap"} gap={4}>
            <HighlightCard
              title="Total Earnings"
              value={`$${totalEarnings.toFixed(2)}`}
              color="#F0F0F0"
            />
            <HighlightCard
              title="Total Hours"
              value={totalHours.toString()}
              color="#F0F0F0"
            />
            <HighlightCard
              title="Average Hourly Rate"
              value={`$${averageHourlyRate.toFixed(2)}`}
              color="#F0F0F0"
            />
          </Box>
          <Box
            mb={2}
            mt={3}
            display="flex"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            <CustomButton
              title="Add Earning"
              handleClick={() => navigate("/earnings/create")}
              backgroundColor="#7982ff"
              color="#fcfcfc"
              icon={<Add />}
            />
            <CustomButton
              title="Download CSV"
              handleClick={handleDownloadCsv}
              backgroundColor="#475be8"
              color="#fcfcfc"
              icon={<FileDownload />}
              disabled={allEarnings.length === 0}
            />
          </Box>
        </Stack>
      </Box>

      <Box mt="20px" borderRadius="30px" overflow="hidden">
        <TableContainer>
          <Table>
            <TableBody
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: "15px",
                padding: "15px",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)"
              }}
            >
              <StyledTableRow>
                <TableCell
                  align="center"
                  style={{ fontWeight: "bold", textTransform: "uppercase" }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color="#f8f9fa"
                    fontSize={{ xs: "12px", md: "16px" }}
                  >
                    Start Date
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  style={{ fontWeight: "bold", textTransform: "uppercase" }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color="#f8f9fa"
                    fontSize={{ xs: "12px", md: "16px" }}
                  >
                    End Date
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  style={{ fontWeight: "bold", textTransform: "uppercase" }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color="#f8f9fa"
                    fontSize={{ xs: "12px", md: "16px" }}
                  >
                    Hours
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  style={{ fontWeight: "bold", textTransform: "uppercase" }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color="#f8f9fa"
                    fontSize={{ xs: "12px", md: "16px" }}
                  >
                    Income
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  style={{ width: "50px" }}
                />
              </StyledTableRow>

              {earningsToShow.map((earning) => (
                <TableRow key={earning._id}>
                  <TableCell align="center">
                    {format(new Date(earning.startDateOfWeek), "dd/MM/yyyy")}
                  </TableCell>
                  <TableCell align="center">
                    {format(new Date(earning.endDateOfWeek), "dd/MM/yyyy")}
                  </TableCell>
                  <TableCell align="center">{earning.weeklyHours}</TableCell>
                  <TableCell align="center">${earning.weeklyIncome}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleDelete(earning._id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {earningsToShow.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={5}>
                    No earnings for this range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Pagination controls */}
        <Box
          mt="20px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {/* Previous button */}
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
            style={{
              marginRight: "10px",
              padding: "8px 16px",
              borderRadius: "5px",
              backgroundColor: currentPage === 1 ? "#ccc" : "#475be8",
              color: "#fff",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              outline: "none",
              border: "none",
            }}
          >
            Previous
          </button>
          {/* Page number */}
          <Typography variant="body1" style={{ margin: "0 10px" }}>
            Page {currentPage} of {totalPages}
          </Typography>
          {/* Next button */}
          <button
            disabled={endIndex >= filteredEarnings.length}
            onClick={() => setCurrentPage(currentPage + 1)}
            style={{
              padding: "8px 16px",
              borderRadius: "5px",
              backgroundColor:
                endIndex >= filteredEarnings.length ? "#ccc" : "#475be8",
              color: "#fff",
              cursor:
                endIndex >= filteredEarnings.length
                  ? "not-allowed"
                  : "pointer",
              outline: "none",
              border: "none",
            }}
          >
            Next
          </button>
        </Box>
      </Box>
    </Box>
  );
};

export default AllEarnings;
